
import { validationMixin } from 'vuelidate';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { format, parse, formatISO } from 'date-fns';
import { defineComponent } from '@nuxtjs/composition-api';
import PublicPortfolioModal from '@/components/partials/public-portfolio/PublicPortfolioModal.vue';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import BravadoTextInputV3 from '@/components/common/BravadoTextInputV3/BravadoTextInputV3.vue';
import BravadoCheckboxV3 from '@/components/common/BravadoCheckboxV3/BravadoCheckboxV3.vue';
import BravadoSelectV3 from '@/components/common/BravadoSelectV3/BravadoSelectV3.vue';
import TrashIcon from '@/assets/svg/trash-opaque.vue.svg';
import PublicPortfolioEducationHistoryDegree from '@/components/partials/public-portfolio/PublicPortfolioEducationHistory/PublicPortfolioEducationHistoryDegree.vue';
import PublicPortfolioEducationHistoryUniversity from '@/components/partials/public-portfolio/PublicPortfolioEducationHistory/PublicPortfolioEducationHistoryUniversity.vue';
import EducationHistoryQuery from '@/components/partials/public-portfolio/PublicPortfolioEducationHistory/apollo/EducationHistory.query';
import EducationHistoryEditMutation from '@/components/partials/public-portfolio/PublicPortfolioEducationHistory/apollo/EducationHistoryEdit.mutation';
import EducationHistoryCreateMutation from '@/components/partials/public-portfolio/PublicPortfolioEducationHistory/apollo/EducationHistoryCreate.mutation';
import EducationHistoryDeleteMutation from '@/components/partials/public-portfolio/PublicPortfolioEducationHistory/apollo/EducationHistoryDelete.mutation';
import { PublicPortfolioCurrentUserQuery } from '@/apollo/queries/PublicPortfolio/PublicPortfolioCurrentUser.query';
import { MONTH_NAMES } from '@/constants/date';
import { validateNumberInput } from '@/components/utils/keys';
import type { EducationHistory, PublicPortfolioCurrentUser } from '@/types/graphql';

type EducationHistoryValues = EducationHistory & {
  isCurrent: boolean;
  startMonth?: string;
  startYear?: string;
  endMonth?: string;
  endYear?: string;
};

const DEFAULT_VALUES: EducationHistoryValues = {
  manuallyEdited: true,
  isCurrent: false,
  university: { name: '', custom: true, id: '', verified: false },
};

const toDate = (date: string) => formatISO(parse(date, 'MMMM/yyyy', new Date()));

export default defineComponent({
  name: 'PublicPortfolioEducationHistoryModal',
  components: {
    PublicPortfolioModal,
    BravadoButtonV3,
    BravadoTextInputV3,
    BravadoCheckboxV3,
    BravadoSelectV3,
    TrashIcon,
    PublicPortfolioEducationHistoryDegree,
    PublicPortfolioEducationHistoryUniversity,
  },
  apollo: {
    jobsInquiryHirings: {
      query: EducationHistoryQuery,
      error(e): void {
        this.$notice.error(e);
      },
      update({ educationHistory }) {
        const startDate = new Date(educationHistory.startDate);
        const endDate = educationHistory.endDate ? new Date(educationHistory.endDate) : undefined;
        this.educationHistory = {
          ...educationHistory,
          isCurrent: !educationHistory.endDate,
          startMonth: educationHistory.manuallyEdited ? format(startDate, 'MMMM') : undefined,
          startYear: format(startDate, 'yyyy'),
          endMonth: educationHistory.manuallyEdited && endDate ? format(endDate, 'MMMM') : undefined,
          endYear: endDate ? format(endDate, 'yyyy') : undefined,
        };
      },
      variables() {
        return {
          id: this.id,
        };
      },
      skip() {
        return !this.isEdit;
      },
    },
  },
  mixins: [validationMixin],
  validations: {
    educationHistory: {
      fieldOfStudy: { required },
      degreeName: { required },
      university: { required: model => !!model.id },
      startYear: { required },
      startMonth: { required },
      endMonth: {
        required: requiredIf(model => !model.isCurrent),
      },
      endYear: {
        required: requiredIf(model => !model.isCurrent),
        greaterThan: (_, values) => {
          if (values.isCurrent || !values.startYear || !values.startMonth || !values.endMonth) return true;
          const startDate = toDate(`${values.startMonth}/${values.startYear}`);
          const endDate = toDate(`${values.endMonth}/${values.endYear}`);
          return endDate > startDate;
        },
      },
    },
  },
  data(): {
    educationHistory: EducationHistoryValues;
    months: Array<{ label: string; value: string }>;
    submitting: boolean;
    attemptedSubmission: boolean;
    deleting: boolean;
  } {
    return {
      educationHistory: {
        ...DEFAULT_VALUES,
      },
      months: MONTH_NAMES.map(month => ({ label: month, value: month })),
      submitting: false,
      attemptedSubmission: false,
      deleting: false,
    };
  },
  computed: {
    id() {
      return this.$route.query.id;
    },
    isEdit() {
      return Boolean(this.id);
    },
    title() {
      if (this.isEdit) return 'Edit Education';
      return 'Add education';
    },
    action() {
      if (this.submitting) return 'Please, wait...';
      return this.isEdit ? 'Update' : 'Save';
    },
    isDisabled() {
      return this.submitting || this.deleting;
    },
  },
  methods: {
    validateYearInput(event: KeyboardEvent): void {
      validateNumberInput(event);
    },

    isFieldValid(field: string) {
      if (this.$apollo.loading) return true;

      const isValid = !this.$v?.educationHistory?.[field]?.$invalid;
      if (!this.educationHistory.manuallyEdited && ['startMonth', 'endMonth'].includes(field)) return isValid;
      if (!this.attemptedSubmission) return true;

      return isValid;
    },

    handleClose() {
      this.educationHistory = { ...DEFAULT_VALUES };
      this.attemptedSubmission = false;
      this.$modal.hide(this.$constants.dynamicModals.PUBLIC_PORTFOLIO_EDUCATION_MODAL);
    },

    async handleUpsert() {
      this.attemptedSubmission = true;
      if (this.$v.$invalid || this.submitting) return;

      const startDate = toDate(`${this.educationHistory.startMonth}/${this.educationHistory.startYear}`);
      const endDate = this.educationHistory.isCurrent
        ? null
        : toDate(`${this.educationHistory.endMonth}/${this.educationHistory.endYear}`);
      const payload = {
        startDate,
        endDate,
        universityId: this.educationHistory.university?.id,
        fieldOfStudy: this.educationHistory.fieldOfStudy,
        degreeName: this.educationHistory.degreeName,
      };

      try {
        this.submitting = true;

        if (this.isEdit) {
          await this.$apollo.mutate({
            mutation: EducationHistoryEditMutation,
            variables: {
              input: {
                id: this.educationHistory.id,
                ...payload,
              },
            },
          });
          await this.$apollo.queries.jobsInquiryHirings.refetch();
          this.$cancelableNotice({ message: 'Education updated' });
        } else {
          await this.$apollo.mutate({
            mutation: EducationHistoryCreateMutation,
            variables: {
              input: payload,
            },
            update(store, result) {
              const { publicPortfolioCurrentUser } = store.readQuery({
                query: PublicPortfolioCurrentUserQuery,
              }) as { publicPortfolioCurrentUser: PublicPortfolioCurrentUser };
              const educationHistory = result.data.createEducationHistoryForPublicPortfolioCurrentUser;
              store.writeQuery({
                query: PublicPortfolioCurrentUserQuery,
                data: {
                  publicPortfolioCurrentUser: {
                    ...publicPortfolioCurrentUser,
                    educationHistories: [...(publicPortfolioCurrentUser.educationHistories ?? []), educationHistory],
                  },
                },
              });
            },
          });
          this.$cancelableNotice({ message: 'Education added to portfolio' });
        }
        this.handleClose();
      } finally {
        this.submitting = false;
      }
    },

    async handleDelete() {
      try {
        const id = this.id;
        this.deleting = true;
        await this.$apollo.mutate({
          mutation: EducationHistoryDeleteMutation,
          variables: {
            input: { id },
          },
          update(store) {
            const { publicPortfolioCurrentUser } = store.readQuery({
              query: PublicPortfolioCurrentUserQuery,
            }) as { publicPortfolioCurrentUser: PublicPortfolioCurrentUser };
            store.writeQuery({
              query: PublicPortfolioCurrentUserQuery,
              data: {
                publicPortfolioCurrentUser: {
                  ...publicPortfolioCurrentUser,
                  educationHistories: (publicPortfolioCurrentUser.educationHistories ?? []).filter(eh => eh.id !== id),
                },
              },
            });
          },
        });
        this.$cancelableNotice({ message: 'Education deleted' });
        this.handleClose();
      } finally {
        this.deleting = false;
      }
    },
  },
});
